import React, { useEffect, useState } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';

import {
  createPasskeyCredential,
  describeDevice,
  PasskeyError
} from '../../../../helpers/passkeys';
import {
  AccountPasskey,
  beginPasskeyRegistration,
  completePasskeyRegistration,
  PasskeyApiError
} from './passkeysApi';
import styles from './PasskeysPanel.module.css';

const keyPrefix = 'account.account-security.passkeys.add-modal';

const defaultName = () => {
  const { browser, os } = describeDevice();
  return browser && os
    ? I18n.t('account.account-security.passkeys.default-name', { browser, os })
    : '';
};

/** The message for a refused confirmation, on adding and on removing a passkey. */
export const confirmationErrorText = (
  error: unknown,
  hasMfaActive: boolean
) => {
  if (error instanceof PasskeyError) {
    return I18n.t(`errors.api.${error.key}`);
  }
  if (error instanceof PasskeyApiError) {
    if (error.key === 'model.invalidCredentials') {
      return I18n.t(
        `${keyPrefix}.${hasMfaActive ? 'invalid-code' : 'invalid-password'}`
      );
    }
    if (error.key === 'model.accountLocked') {
      return I18n.t(`${keyPrefix}.account-locked`);
    }
  }
  return I18n.t('general.general-error-message');
};

interface AddPasskeyModalProps {
  accountId: number | string;
  /** An account with MFA confirms with a code only; without MFA, with its password only. */
  hasMfaActive: boolean;
  open: boolean;
  onAdded: (passkey: AccountPasskey) => void;
  onClose: () => void;
}

const AddPasskeyModal = ({
  accountId,
  hasMfaActive,
  open,
  onAdded,
  onClose
}: AddPasskeyModalProps) => {
  const [secret, setSecret] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSecret('');
      setName(defaultName());
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const options = await beginPasskeyRegistration(
        accountId,
        hasMfaActive ? { otp: secret } : { password: secret }
      );
      const credential = await createPasskeyCredential(options.options);
      const passkey = await completePasskeyRegistration(accountId, {
        challengeId: options.challengeId,
        credential,
        name
      });
      onAdded(passkey);
    } catch (caught) {
      setError(confirmationErrorText(caught, hasMfaActive));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      className="wizard"
      size="tiny"
      dimmer="inverted"
      closeIcon={!busy}
      closeOnDimmerClick={!busy}
      closeOnEscape={!busy}
      open={open}
      onClose={onClose}
    >
      <Modal.Header>
        <h3>{I18n.t(`${keyPrefix}.title`)}</h3>
      </Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit} error={!!error}>
          <p>{I18n.t(`${keyPrefix}.confirm-instructions`)}</p>
          <Form.Field>
            <label htmlFor="passkey-confirmation">
              {I18n.t(
                `${keyPrefix}.${hasMfaActive ? 'code-label' : 'password-label'}`
              )}
            </label>
            <input
              id="passkey-confirmation"
              className={styles.input}
              type={hasMfaActive ? 'text' : 'password'}
              inputMode={hasMfaActive ? 'numeric' : undefined}
              autoComplete={hasMfaActive ? 'one-time-code' : 'current-password'}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="passkey-name">
              {I18n.t(`${keyPrefix}.name-label`)}
            </label>
            <input
              id="passkey-name"
              type="text"
              className={styles.input}
              maxLength={100}
              value={name}
              aria-describedby="passkey-name-help"
              onChange={(e) => setName(e.target.value)}
            />
            <small id="passkey-name-help">
              {I18n.t(`${keyPrefix}.name-help`)}
            </small>
          </Form.Field>
          {error && <Message error role="alert" content={error} />}
          {busy && <p role="status">{I18n.t(`${keyPrefix}.prompt`)}</p>}
          <div
            className="button-wrapper align-right"
            style={{ marginTop: '16px' }}
          >
            <Button type="button" onClick={onClose} disabled={busy}>
              {I18n.t('account.account-security.passkeys.cancel')}
            </Button>
            <Button
              primary
              type="submit"
              loading={busy}
              disabled={busy || !secret}
            >
              {I18n.t(`${keyPrefix}.continue`)}
            </Button>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export default AddPasskeyModal;
